import { useEffect } from "react";
import { useFloatingIconsContext } from "@/contexts/FloatingIconsContext";
import { OPEN_ACTIVITY_POPUP_EVENT } from "@/modules/homeDisplay/hooks";
import { ActivityType } from "@/modules/homeDisplay/types/homeDisplay.types";
import { useWheelEntryClick } from "@/modules/wheel/hooks";
import { useUserStore } from "@/modules/user/stores";
import { openLoginRegisterPopup } from "@/components/LoginRegisterPopup";

/**
 * 活动弹窗桥：把「首页活动入口 / 活动小 banner / 主 Banner 里配的『打开活动弹窗』」
 * 转成对应浮标的点击。
 *
 * 为什么需要它：
 *  · useDisplayItemClick 的 switch 只显式处理了 6 种活动类型，其余（首充、五充、红包雨、
 *    充值解锁余额、充值优惠、首充分批…）原来落到 default 只打一行 log，运营配了点着没反应。
 *  · 这些活动"怎么打开"在 useFloatingIcons 里已经实现过一遍了，且每个 onClick 背后都是带活动
 *    上下文的 trigger（会挑中具体 banner / task）。照抄成裸 triggerPopup 容易弹出空数据的窗，
 *    所以直接复用那份 onClick。
 *  · 必须走事件而不是让页面组件自己读 context：App.tsx 里 <AppRouter /> 和
 *    <FloatingIconsProvider> 是兄弟节点，页面内组件在 provider 之外，读到的是空 fallback。
 *    同一手法见 OPEN_RESCUE_FUND_EVENT。
 *
 * 必须挂在 FloatingIconsProvider 子树内（App.tsx 里与 GlobalEvent 同级）。
 */
export const ActivityPopupBridge = () => {
  const { floatingIcons, leftBottomFloatingIcons } = useFloatingIconsContext();
  // 转盘弹窗由 GlobalEvent 里的 useWheelActivityPopup 注册，只要转盘启用就能手动触发，
  // 与「转盘的浮动图标入口有没有勾」无关 —— 故单独处理，不走下面的浮标反查。
  const wheelEntryClick = useWheelEntryClick();
  // 未登录时找不到活动入口 → 多半是「该活动要登录才可见」，引导去登录（见下方 !target 分支）
  const isAuthenticated = useUserStore(state => state.isAuthenticated());

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail as
        | { activityId?: string; activityType?: ActivityType }
        | undefined;
      const activityId = detail?.activityId;
      const activityType = detail?.activityType;

      if (activityType === ActivityType.LUCKY_WHEEL) {
        wheelEntryClick();
        return;
      }

      // 右下角优先：活动类浮标基本都在右下角；左下角作为补充（部分活动配在那边）
      const pool = [
        ...(floatingIcons ?? []),
        ...(leftBottomFloatingIcons ?? []),
      ];

      // 先按 activityId 精确匹配（同一活动类型可能有多条 banner，比如充值活动分多组）；
      // 匹配不到再退到活动类型 —— 后台「活动入口」里存的 activityId 可能是初始化时写的
      // 固定值（形如 XXX_FIXED），与浮标 banner 上的 activityId 不一致。
      const target =
        (activityId &&
          pool.find(
            icon => icon.activityId && icon.activityId === activityId
          )) ||
        (activityType &&
          pool.find(icon => icon.activityType === activityType)) ||
        null;

      if (!target) {
        /**
         * 找不到可用入口分两种情况：
         *
         *   · **未登录** —— 绝大多数活动浮标的 shouldShow 依赖用户维度数据
         *     （是否绑定活动、是否可领取…），游客态本就构造不出这个入口。
         *     此时不该静默 —— 拉起登录弹窗（登录态，不是注册），登录后用户可再点一次。
         *   · **已登录** —— 说明这个活动当前确实不满足展示条件（未开启 / 已结束 /
         *     该用户不可见），弹个空窗反而更糟，保持静默并留日志便于排查。
         *
         * 注意：只有「打开活动弹窗」类型才会走到这里；跳转链接与自定义文案在
         * useDisplayItemClick 里就分发掉了，不受影响。
         */
        if (!isAuthenticated) {
          openLoginRegisterPopup({ defaultMode: "login" });
          return;
        }
        console.warn(
          "[ActivityPopupBridge] 未找到可用的活动入口",
          activityId,
          activityType
        );
        return;
      }
      target.onClick();
    };

    window.addEventListener(OPEN_ACTIVITY_POPUP_EVENT, handler);
    return () => window.removeEventListener(OPEN_ACTIVITY_POPUP_EVENT, handler);
  }, [
    floatingIcons,
    leftBottomFloatingIcons,
    wheelEntryClick,
    isAuthenticated,
  ]);

  return null;
};

export default ActivityPopupBridge;
